import { useEffect, useRef, useState } from 'react';

const MAX_BYTES = 20 * 1024 * 1024;

export function filesFromList(fileList) {
  return [...(fileList ?? [])].filter((file) => file && file.size >= 0);
}

export function splitBySize(files) {
  const accepted = [];
  const tooLarge = [];
  for (const file of files) {
    if (file.size > MAX_BYTES) tooLarge.push(file);
    else accepted.push(file);
  }
  return { accepted, tooLarge };
}

export function mergeFiles(current, incoming) {
  const next = [...current];
  for (const file of incoming) {
    const exists = next.some(
      (item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified,
    );
    if (!exists) next.push(file);
  }
  return next;
}

function isFileDrag(event) {
  const types = event.dataTransfer?.types;
  return Boolean(types && [...types].includes('Files'));
}

/**
 * Accepts OS file drags on the page root while mounted.
 * preventDefault stops the browser from opening the dropped file.
 */
export function usePageFileDrop(onFiles) {
  const rootRef = useRef(null);
  const [active, setActive] = useState(false);
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const setDragging = (value) => {
      setActive((current) => (current === value ? current : value));
    };

    const onDragEnter = (event) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      setDragging(true);
    };

    const onDragOver = (event) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      setDragging(true);
    };

    const onDragLeave = (event) => {
      if (!isFileDrag(event)) return;
      const next = event.relatedTarget;
      if (!next || !root.contains(next)) setDragging(false);
    };

    const onDrop = (event) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      setDragging(false);
      const files = filesFromList(event.dataTransfer?.files);
      if (files.length) onFilesRef.current(files);
    };

    root.addEventListener('dragenter', onDragEnter);
    root.addEventListener('dragover', onDragOver);
    root.addEventListener('dragleave', onDragLeave);
    root.addEventListener('drop', onDrop);
    return () => {
      root.removeEventListener('dragenter', onDragEnter);
      root.removeEventListener('dragover', onDragOver);
      root.removeEventListener('dragleave', onDragLeave);
      root.removeEventListener('drop', onDrop);
    };
  }, []);

  return { active, rootRef };
}

export function AssetFileDropzone({ active = false, busy = false, onBrowse, title, hint }) {
  return (
    <label className={`dropzone${active ? ' is-active' : ''}${busy ? ' is-busy' : ''}`}>
      <input
        type="file"
        multiple
        disabled={busy}
        onChange={(event) => {
          const files = filesFromList(event.target.files);
          if (files.length) onBrowse(files);
          event.target.value = '';
        }}
      />
      <strong>{busy ? 'Uploading…' : title || 'Drag and drop files here'}</strong>
      <span className="muted small">{hint || 'or click to browse'}</span>
    </label>
  );
}

export function SelectedUploadFiles({ files, onRemove, disabled }) {
  if (!files.length) return null;
  return (
    <ul className="list-plain">
      {files.map((file) => (
        <li key={`${file.name}-${file.size}-${file.lastModified}`} className="file-row">
          <span style={{ flex: 1, minWidth: 0 }}>{file.name}</span>
          <button type="button" className="btn-link small" disabled={disabled} onClick={() => onRemove(file)}>
            Remove
          </button>
        </li>
      ))}
    </ul>
  );
}
